// Raw HTTP transport for the deal-flow controllers. Hooks in
// @/hooks/dealFlow wrap these with TanStack Query.

import { api } from "../client";
import type {
  AdminUndoLogDto,
  AdminUndoRequest,
  AdminUndoResultDto,
  CertifyEscrowFundingVerifiedRequest,
  CertifyEscrowIssuedRequest,
  CreateCatalogItemRequest,
  CreateCertificationTemplateRequest,
  CriteriaCatalogItemDto,
  CriteriaChecklistItemDto,
  CriteriaLane,
  CriteriaTier,
  DealAssignmentDto,
  DealPaymentSetupDto,
  DealPaymentSetupParty,
  DealPaymentSetupPartyDto,
  DealTpsAssignment,
  DealTpsOption,
  SetDealTpsRequest,
  DealFailureDto,
  DealFlowOverviewResponse,
  DealFlowTab,
  DealGateBypassLogDto,
  DealGateBypassResultDto,
  DealOutcomeDto,
  EscalationDetailDto,
  EscalationListQuery,
  EscalationRecordDto,
  PagedEscalations,
  PagedFailures,
  DeclineChecklistItemRequest,
  DealEscrowStatusDto,
  EscrowSetupDto,
  FailureListQuery,
  FinalFinancialAddendumDto,
  IssueLaneAssessmentRequest,
  LaneAssessmentDto,
  LoanCreationResultDto,
  CreateLoanPreviewDto,
  LoanDocumentIssuanceDto,
  ReleaseEscrowRequest,
  CertifyCertificationRequest,
  DeclineCertificationRequest,
  EscalateCertificationRequest,
  ResolveEscalationRequest,
  ResolveFailureRequest,
  SaveCertificationStateRequest,
  SignCertificationRequest,
  UnderwritingCertificationDto,
  UnderwritingCertificationTemplateDto,
  UpdateCatalogItemRequest,
  UpdateCertificationTemplateRequest,
  UpdateFailureRequest,
  UpdateJustificationRequest,
  UpsertAssignmentRequest,
  UpsertEscrowSetupRequest,
  UpsertFinalFinancialAddendumRequest,
  UpsertLoanDocumentIssuanceRequest,
  WaiveChecklistItemRequest,
  ResetChecklistItemRequest,
} from "./types";

// ---- catalog ---------------------------------------------------

export const catalogApi = {
  list: (lane?: CriteriaLane, tier?: CriteriaTier, isActive = true) =>
    api.get<CriteriaCatalogItemDto[]>("/deal-flow/catalog", {
      lane,
      tier,
      isActive,
    }),
  getByCode: (code: string) =>
    api.get<CriteriaCatalogItemDto>(`/deal-flow/catalog/${encodeURIComponent(code)}`),
  /** ADMIN-only template edit. Edits propagate to every deal. */
  update: (id: string, body: UpdateCatalogItemRequest) =>
    api.patch<CriteriaCatalogItemDto>(`/deal-flow/catalog/${id}`, body),
  /** ADMIN-only add. The code is generated server-side; the new active item
   *  appears on every deal on the next analyst-tab load. */
  create: (body: CreateCatalogItemRequest) =>
    api.post<CriteriaCatalogItemDto>("/deal-flow/catalog", body),
};

// ---- assignments ----------------------------------------------

export const assignmentsApi = {
  list: (dealId: string) =>
    api.get<DealAssignmentDto[]>(`/deals/${dealId}/assignments`),
  upsert: (dealId: string, roleCode: string, body: UpsertAssignmentRequest) =>
    api.put<DealAssignmentDto>(`/deals/${dealId}/assignments/${roleCode}`, body),
  markNa: (dealId: string, roleCode: string) =>
    api.post<DealAssignmentDto>(`/deals/${dealId}/assignments/${roleCode}/n-a`),
};

// ---- checklist -------------------------------------------------

export const checklistApi = {
  list: (dealId: string, lane: CriteriaLane) =>
    api.get<CriteriaChecklistItemDto[]>(`/deals/${dealId}/checklist`, { lane }),
  get: (dealId: string, itemId: string) =>
    api.get<CriteriaChecklistItemDto>(`/deals/${dealId}/checklist/${itemId}`),
  saveJustification: (
    dealId: string,
    itemId: string,
    body: UpdateJustificationRequest,
  ) => api.put<CriteriaChecklistItemDto>(`/deals/${dealId}/checklist/${itemId}`, body),
  certify: (dealId: string, itemId: string) =>
    api.post<CriteriaChecklistItemDto>(`/deals/${dealId}/checklist/${itemId}/certify`),
  escalate: (dealId: string, itemId: string, escalationReason: string) =>
    api.post<CriteriaChecklistItemDto>(
      `/deals/${dealId}/checklist/${itemId}/escalate`,
      { escalationReason },
    ),
  resetToPending: (dealId: string, itemId: string) =>
    api.post<CriteriaChecklistItemDto>(
      `/deals/${dealId}/checklist/${itemId}/reset-to-pending`,
    ),
  decline: (dealId: string, itemId: string, body: DeclineChecklistItemRequest) =>
    api.post<CriteriaChecklistItemDto>(`/deals/${dealId}/checklist/${itemId}/decline`, body),
  waive: (dealId: string, itemId: string, body: WaiveChecklistItemRequest) =>
    api.post<CriteriaChecklistItemDto>(`/deals/${dealId}/checklist/${itemId}/waive`, body),
  reset: (dealId: string, itemId: string, body: ResetChecklistItemRequest) =>
    api.post<CriteriaChecklistItemDto>(`/deals/${dealId}/checklist/${itemId}/reset`, body),
};

// ---- lane assessments -----------------------------------------

export const laneAssessmentsApi = {
  list: (dealId: string) =>
    api.get<LaneAssessmentDto[]>(`/deals/${dealId}/assessments`),
  get: (dealId: string, lane: CriteriaLane) =>
    api.get<LaneAssessmentDto>(`/deals/${dealId}/assessments/${lane}`),
  issue: (dealId: string, lane: CriteriaLane, body: IssueLaneAssessmentRequest) =>
    api.post<LaneAssessmentDto>(`/deals/${dealId}/assessments/${lane}/issue`, body),
  reissue: (dealId: string, lane: CriteriaLane, body: IssueLaneAssessmentRequest) =>
    api.post<LaneAssessmentDto>(`/deals/${dealId}/assessments/${lane}/reissue`, body),
};

// ---- deal outcome ---------------------------------------------

export const dealOutcomeApi = {
  get: (dealId: string) => api.get<DealOutcomeDto>(`/deals/${dealId}/outcome`),
  finalize: (dealId: string) =>
    api.post<DealOutcomeDto>(`/deals/${dealId}/outcome/finalize`),
};

// ---- failures --------------------------------------------------

export const failuresApi = {
  list: (query?: FailureListQuery) =>
    api.get<PagedFailures>("/deal-flow/failures", query),
  get: (id: string) => api.get<DealFailureDto>(`/deal-flow/failures/${id}`),
  update: (id: string, body: UpdateFailureRequest) =>
    api.patch<DealFailureDto>(`/deal-flow/failures/${id}`, body),
  markReviewed: (id: string, notes: string | null) =>
    api.post<DealFailureDto>(`/deal-flow/failures/${id}/review`, { notes }),
  resolve: (id: string, body: ResolveFailureRequest) =>
    api.post<DealFailureDto>(`/deal-flow/failures/${id}/resolve`, body),
  /** Returns a relative URL suitable for an &lt;a download&gt; link.
   *  The backend rate-limits this to 1 request per user per 60 seconds and
   *  returns 429 with a Retry-After header when exceeded. */
  exportCsvUrl: (query?: FailureListQuery) => {
    const params = new URLSearchParams();
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== null && v !== "" && k !== "page" && k !== "pageSize") {
          params.append(k, String(v));
        }
      }
    }
    const qs = params.toString();
    return `/api/deal-flow/failures/export.csv${qs ? `?${qs}` : ""}`;
  },
};

// ---- deal-flow overview tab payload -----------------------------

export const dealFlowOverviewApi = {
  get: (dealId: string) =>
    api.get<DealFlowOverviewResponse>(`/deals/${dealId}/overview-deal-flow`),
};

// ---- escrow ----------------------------------------------------

export const escrowApi = {
  get: (dealId: string) => api.get<EscrowSetupDto>(`/deals/${dealId}/escrow`),
  upsert: (dealId: string, body: UpsertEscrowSetupRequest) =>
    api.put<EscrowSetupDto>(`/deals/${dealId}/escrow`, body),
  issue: (dealId: string, body: CertifyEscrowIssuedRequest) =>
    api.post<EscrowSetupDto>(`/deals/${dealId}/escrow/issue`, body),
  verifyFunding: (dealId: string, body: CertifyEscrowFundingVerifiedRequest) =>
    api.post<EscrowSetupDto>(`/deals/${dealId}/escrow/verify-funding`, body),
  // Either escrow model: the server routes by the deal's model.
  release: (dealId: string, body: ReleaseEscrowRequest) =>
    api.post<DealEscrowStatusDto>(`/deals/${dealId}/escrow/release`, body),
  status: (dealId: string) =>
    api.get<DealEscrowStatusDto>(`/deals/${dealId}/escrow/status`),
};

// ---- certifications -------------------------------------------

export const certificationsApi = {
  list: (dealId: string, tab: DealFlowTab, section: number) =>
    api.get<UnderwritingCertificationDto[]>(`/deals/${dealId}/certifications`, {
      tab,
      section,
    }),
  /** Legacy SIGN ceremony — retained for the Underwriting tab. */
  sign: (dealId: string, body: SignCertificationRequest) =>
    api.post<UnderwritingCertificationDto>(`/deals/${dealId}/certifications`, body),
  /** Section-2 CERTIFY action. */
  certify: (dealId: string, body: CertifyCertificationRequest) =>
    api.post<UnderwritingCertificationDto>(
      `/deals/${dealId}/certifications/certify`,
      body,
    ),
  /** Section-2 DECLINE action. */
  decline: (dealId: string, body: DeclineCertificationRequest) =>
    api.post<UnderwritingCertificationDto>(
      `/deals/${dealId}/certifications/decline`,
      body,
    ),
  /** Section-2 ESCALATE action. */
  escalate: (dealId: string, body: EscalateCertificationRequest) =>
    api.post<UnderwritingCertificationDto>(
      `/deals/${dealId}/certifications/escalate`,
      body,
    ),
  /** Persists in-progress OPEN-FORM state without a
   *  terminal transition (mirrors the Section-1 Save and close). */
  saveDraft: (dealId: string, body: SaveCertificationStateRequest) =>
    api.post<UnderwritingCertificationDto>(
      `/deals/${dealId}/certifications/save`,
      body,
    ),
};

// ---- loan document issuance + final financial addendum -------

export const loanDocumentIssuanceApi = {
  get: (dealId: string) =>
    api.get<LoanDocumentIssuanceDto>(`/deals/${dealId}/loan-document-issuance`),
  upsert: (dealId: string, body: UpsertLoanDocumentIssuanceRequest) =>
    api.put<LoanDocumentIssuanceDto>(`/deals/${dealId}/loan-document-issuance`, body),
};

export const finalFinancialAddendumApi = {
  get: (dealId: string) =>
    api.get<FinalFinancialAddendumDto>(`/deals/${dealId}/final-financial-addendum`),
  upsert: (dealId: string, body: UpsertFinalFinancialAddendumRequest) =>
    api.put<FinalFinancialAddendumDto>(`/deals/${dealId}/final-financial-addendum`, body),
};

// ---- Closing Step 2 — deal payment-setup status ---------------------

export const dealPaymentSetupApi = {
  get: (dealId: string) =>
    api.get<DealPaymentSetupDto>(`/deals/${dealId}/payment-setup`),
  /** Re-link a party to a portal account (staff override); pass null to clear
   *  the override and revert to the deal-inherited account. */
  relinkAccount: (
    dealId: string,
    party: DealPaymentSetupParty,
    portalUserId: string | null,
  ) =>
    api.put<DealPaymentSetupPartyDto>(
      `/deals/${dealId}/payment-setup/${party}/account`,
      { portalUserId },
    ),
  /** Manual "Send reminder" — bumps lastReminderSentAt. */
  remind: (dealId: string, party: DealPaymentSetupParty) =>
    api.post<DealPaymentSetupPartyDto>(
      `/deals/${dealId}/payment-setup/${party}/remind`,
    ),
};

// ---- Closing Step 2 — servicing platform (TPS) assignment -----------

export const dealTpsAssignmentApi = {
  get: (dealId: string) =>
    api.get<DealTpsAssignment>(`/deals/${dealId}/tps-assignment`),
  options: (dealId: string) =>
    api.get<DealTpsOption[]>(`/deals/${dealId}/tps-assignment/options`),
  set: (dealId: string, body: SetDealTpsRequest) =>
    api.put<DealTpsAssignment>(`/deals/${dealId}/tps-assignment`, body),
};

// ---- create-loan --------------------------------------

export const closingApi = {
  createLoan: (dealId: string, firstPaymentAmount?: number) =>
    api.post<LoanCreationResultDto>(`/deals/${dealId}/create-loan`, { firstPaymentAmount }),
  createLoanPreview: (dealId: string) =>
    api.get<CreateLoanPreviewDto>(`/deals/${dealId}/create-loan/preview`),
};

// ---- escalations ----------------------------

export const escalationsApi = {
  list: (query?: EscalationListQuery) =>
    api.get<PagedEscalations>("/deal-flow/escalations", query),
  get: (id: string) =>
    api.get<EscalationDetailDto>(`/deal-flow/escalations/${id}`),
  approve: (id: string, body: ResolveEscalationRequest) =>
    api.post<EscalationRecordDto>(`/deal-flow/escalations/${id}/approve`, body),
  deny: (id: string, body: ResolveEscalationRequest) =>
    api.post<EscalationRecordDto>(`/deal-flow/escalations/${id}/deny`, body),
  exportCsvUrl: (query?: EscalationListQuery) => {
    const params = new URLSearchParams();
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== null && v !== "" && k !== "page" && k !== "pageSize") {
          params.append(k, String(v));
        }
      }
    }
    const qs = params.toString();
    return `/api/deal-flow/escalations/export.csv${qs ? `?${qs}` : ""}`;
  },
};

// ---- Section 2 certification templates -----

export const certificationTemplatesApi = {
  /** Section 2 certification templates for an analyst tab — drives the
   *  certification list + the Section 3 completion gate. */
  listByTab: (tab: DealFlowTab) =>
    api.get<UnderwritingCertificationTemplateDto[]>(
      "/deal-flow/certification-templates",
      { tab },
    ),
  get: (certificationCode: string) =>
    api.get<UnderwritingCertificationTemplateDto>(
      `/deal-flow/certification-templates/${encodeURIComponent(certificationCode)}`,
    ),
  /** ADMIN-only template edit. Edits propagate to every deal. */
  update: (certificationCode: string, body: UpdateCertificationTemplateRequest) =>
    api.patch<UnderwritingCertificationTemplateDto>(
      `/deal-flow/certification-templates/${encodeURIComponent(certificationCode)}`,
      body,
    ),
  /** ADMIN-only add. The full code is derived server-side from tab + key; the
   *  new template appears on every deal on the next analyst-tab load. */
  create: (body: CreateCertificationTemplateRequest) =>
    api.post<UnderwritingCertificationTemplateDto>(
      "/deal-flow/certification-templates",
      body,
    ),
};

// ---- Admin Undo -------------------------

export const adminUndoApi = {
  section9Handoff: (dealId: string, body: AdminUndoRequest) =>
    api.post<AdminUndoResultDto>(
      `/deal-flow/admin-undo/section-9-handoff/${dealId}`,
      body,
    ),
  s8CertSign: (dealId: string, certificationCode: string, body: AdminUndoRequest) =>
    api.post<AdminUndoResultDto>(
      `/deal-flow/admin-undo/s8-cert-sign/${dealId}?certificationCode=${encodeURIComponent(certificationCode)}`,
      body,
    ),
  dealOutcomeFinalize: (dealId: string, body: AdminUndoRequest) =>
    api.post<AdminUndoResultDto>(
      `/deal-flow/admin-undo/deal-outcome-finalize/${dealId}`,
      body,
    ),
  laneAssessment: (dealId: string, lane: CriteriaLane, body: AdminUndoRequest) =>
    api.post<AdminUndoResultDto>(
      `/deal-flow/admin-undo/lane-assessment/${dealId}/${lane}`,
      body,
    ),
  criteriaItemTerminal: (checklistItemId: string, body: AdminUndoRequest) =>
    api.post<AdminUndoResultDto>(
      `/deal-flow/admin-undo/criteria-item-terminal/${checklistItemId}`,
      body,
    ),
  analystS2Certification: (certificationId: string, body: AdminUndoRequest) =>
    api.post<AdminUndoResultDto>(
      `/deal-flow/admin-undo/analyst-s2-certification/${certificationId}`,
      body,
    ),
  closingS1Certification: (certificationId: string, body: AdminUndoRequest) =>
    api.post<AdminUndoResultDto>(
      `/deal-flow/admin-undo/closing-s1-certification/${certificationId}`,
      body,
    ),
  escrowIssuance: (dealId: string, body: AdminUndoRequest) =>
    api.post<AdminUndoResultDto>(
      `/deal-flow/admin-undo/escrow-issuance/${dealId}`,
      body,
    ),
  escrowFunding: (dealId: string, body: AdminUndoRequest) =>
    api.post<AdminUndoResultDto>(
      `/deal-flow/admin-undo/escrow-funding/${dealId}`,
      body,
    ),
  listForDeal: (dealId: string) =>
    api.get<AdminUndoLogDto[]>(`/deal-flow/admin-undo/by-deal/${dealId}`),
};

// ---- Closing-gate Bypass (Escrow) -------------

export const dealGateBypassApi = {
  /** ADMIN-only break-glass — force-complete the Escrow Setup gate for a TEST
   *  deal. 400 (reason < 5 chars), 404 (deal not found), 422 (deal already has
   *  real escrow activity — surface the message verbatim). */
  bypassEscrow: (dealId: string, reason: string) =>
    api.post<DealGateBypassResultDto>(
      `/deal-flow/gate-bypass/escrow/${dealId}`,
      { reason },
    ),
  /** Bypass history for a single deal, most-recent first. */
  listForDeal: (dealId: string) =>
    api.get<DealGateBypassLogDto[]>(`/deal-flow/gate-bypass/by-deal/${dealId}`),
  /** Cross-deal recent bypasses, most-recent first. */
  listRecent: (take = 100) =>
    api.get<DealGateBypassLogDto[]>("/deal-flow/gate-bypass/recent", { take }),
};
